import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import { Image } from "../../../AbstractElements";
import { dynamicImage } from "../../../Utils";
import moment from "moment";
import { getData, postData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import CommonTableNew from "../../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import MonthDropdown from "../../CommonCompo/MonthDropdown";
import { getLabel } from "../../../sharedFunctions/CommonLable";
import { isOwnRequest } from "../../../Utils/authUtils";
import { toast } from "react-toastify";

interface UserExpensesProps {
  employeeIdProp: number;
}

const UserExpenses: React.FC<UserExpensesProps> = ({ employeeIdProp }) => {
  const navigate = useNavigate();
  const serverUrl = localStorage.getItem("serverUrl") || "";

  const [loading, setLoading] = useState<boolean>(true);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedRange, setSelectedRange] = useState<string>("");

  const [expenseData, setExpenseData] = useState<any[]>([]);
  const [statusMap, setStatusMap] = useState<Record<number, string>>({});
  const [workingDays, setWorkingDays] = useState<number | null>(null);
  const [totalExpenseAmount, setTotalExpenseAmount] = useState<number>(0);
  const [totalExpCount, setTotalExpCount] = useState<number>(0);
  const [selectedExpIds, setSelectedExpIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(true);

  const [submitConfirmationVisible, setSubmitConfirmationVisible] = useState(false);
  const [submitRemarkModalVisible, setSubmitRemarkModalVisible] = useState(false);
  const [submitRemark, setSubmitRemark] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(false);

  const [lastDates, setLastDates] = useState({
    LastSubmittedDate: null,
    LastApprovedDate: null,
    LastAuditedDate: null,
  });

  const handleMonthChange = (formattedMonth: string, rawKey?: string) => {
    setSelectedMonth(formattedMonth);
    setSelectedRange(rawKey || "");
  };

  const activeEmpId = employeeIdProp || Number(localStorage.getItem("empId")) || 0;
  const isManagerView = useMemo(() => {
    return !isOwnRequest(activeEmpId);
  }, [activeEmpId]);

  const selectedMonthKey = selectedRange || selectedMonth || "";

  const allRowExpIds = useMemo(() => {
    return (expenseData || [])
      .filter((item) => !item?.hasOwnProperty("Type") && (item?.expenses?.ExpId || item?.ExpId))
      .map((item) => String(item?.expenses?.ExpId || item?.ExpId));
  }, [expenseData]);

  // When expenseData loads, select all valid expense rows by default ("select all default")
  useEffect(() => {
    if (expenseData && expenseData.length > 0) {
      const allIds = expenseData
        .filter((item) => !item?.hasOwnProperty("Type") && (item?.expenses?.ExpId || item?.ExpId))
        .map((item) => String(item?.expenses?.ExpId || item?.ExpId));
      setSelectedExpIds(allIds);
      setSelectAll(true);
    } else {
      setSelectedExpIds([]);
      setSelectAll(false);
    }
  }, [expenseData]);

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedExpIds(allRowExpIds);
      setSelectAll(true);
    } else {
      setSelectedExpIds([]);
      setSelectAll(false);
    }
  };

  const handleCheckboxToggle = () => {
    // "cant diselect one one select mean select all": clicking any checkbox toggles all checkboxes together
    toggleSelectAll(!selectAll);
  };

  const allSubmittableIds = useMemo(() => {
    return (expenseData || [])
      .filter((item) => {
        const isSummaryRow = Object.prototype.hasOwnProperty.call(item || {}, "Type");
        const expId = String(item?.expenses?.ExpId ?? item?.ExpId ?? "");
        if (isSummaryRow || !expId) return false;

        const rawStatus = item?.expenses?.ExpenseStatus ?? item?.ExpenseStatus;
        const status = statusMap?.[rawStatus] || statusMap?.[Number(rawStatus)];
        return (
          Number(rawStatus) === 1 ||
          Number(rawStatus) === 4 ||
          status === "Created" ||
          status === "Rejected"
        );
      })
      .map((item) => String(item?.expenses?.ExpId ?? item?.ExpId));
  }, [expenseData, statusMap]);

  const allCheckboxesSelected = useMemo(() => {
    return selectAll && allRowExpIds.length > 0 && selectedExpIds.length === allRowExpIds.length;
  }, [selectAll, allRowExpIds.length, selectedExpIds.length]);

  const selectableExpenseIds = useMemo(() => {
    return allSubmittableIds.filter((id) => selectedExpIds.includes(id));
  }, [allSubmittableIds, selectedExpIds]);

  const actualExpCount = useMemo(() => {
    if (totalExpCount > 0) return totalExpCount;
    return (expenseData || []).filter(
      (item) => !item?.hasOwnProperty("Type") && (item?.expenses?.ExpId || item?.ExpId)
    ).length;
  }, [totalExpCount, expenseData]);

  const actualWorkingDays = Number(workingDays || 0);

  // If submittable expenses exist and total expense count is less than working days -> Some dates are pending for attendance!
  const showPendingAttendanceWarning = useMemo(() => {
    return allSubmittableIds.length > 0 && actualWorkingDays > 0 && actualExpCount < actualWorkingDays;
  }, [allSubmittableIds.length, actualWorkingDays, actualExpCount]);

  // Can submit expenses only after all checkboxes are selected and attendance count >= working days
  const canSubmitExpenses = useMemo(() => {
    return (
      allSubmittableIds.length > 0 &&
      allCheckboxesSelected &&
      (actualWorkingDays === 0 || actualExpCount >= actualWorkingDays)
    );
  }, [allSubmittableIds.length, allCheckboxesSelected, actualWorkingDays, actualExpCount]);

  const handleSubmitButtonClick = () => {
    if (!allCheckboxesSelected) {
      toast.warning("Please select all checkboxes to submit expenses.");
      return;
    }
    if (actualWorkingDays > 0 && actualExpCount < actualWorkingDays) {
      toast.warning("Some dates are pending for attendance. Please complete attendance before submitting.");
      return;
    }
    setSubmitConfirmationVisible(true);
  };

  const submitExpenseAction = async (status: number, note: string = "") => {
    const targetIds = selectableExpenseIds.length > 0 ? selectableExpenseIds : allSubmittableIds;
    if (targetIds.length === 0) {
      toast.warning("No expenses available to submit.");
      return;
    }
    setIsSubmitting(true);
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
        serverBaseUrl += "/";
      }
      const url = `${serverBaseUrl}${API.expensesApproved}`;
      const params = {
        ExpId: targetIds,
        status,
        note,
      };
      const { status: respStatus, data } = await postData(url, true, params);
      if (respStatus === 200) {
        toast.success(data?.message || "Expenses submitted successfully!");
        setSubmitConfirmationVisible(false);
        setSubmitRemarkModalVisible(false);
        setSubmitRemark("");
        setRefreshKey((prev) => !prev);
      } else {
        toast.warning(data?.message || "Failed to submit expenses.");
      }
    } catch (error) {
      console.error("Error submitting expense:", error);
      toast.error("An error occurred while submitting expenses.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch Status Map on mount
  useEffect(() => {
    const fetchStatusMap = async () => {
      try {
        let serverBaseUrl = serverUrl;
        if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
          serverBaseUrl += "/";
        }

        const statusUrl = `${serverBaseUrl}${API.getStatus}`;
        const statusRes = await getData(statusUrl, true);
        if (statusRes.status === 200 && statusRes.data?.data?.[0]?.Expenses) {
          const mapObj = statusRes.data.data[0].Expenses;
          const parsed: Record<number, string> = {};
          Object.keys(mapObj).forEach((k) => {
            parsed[parseInt(k, 10)] = mapObj[k];
          });
          setStatusMap(parsed);
        }
      } catch (err) {
        console.error("Error fetching status map:", err);
      }
    };

    fetchStatusMap();
  }, [serverUrl]);

  // Fetch expenses for selected month and employee
  useEffect(() => {
    if (!selectedMonth && !selectedRange) return;

    const fetchMonthlyExpenses = async () => {
      setLoading(true);
      try {
        let serverBaseUrl = serverUrl;
        if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
          serverBaseUrl += "/";
        }

        const monthParam = selectedRange || selectedMonth;
        const formattedMonthParam =
          selectedMonth ||
          (selectedRange.includes("|")
            ? moment(selectedRange.split("|")[0], "YYYY-MM-DD").format("MM-YYYY")
            : selectedRange);

        // 1. Fetch Expenses List
        const listUrl = `${serverBaseUrl}${API.getExpensesNew}`;
        const listParams = {
          month: monthParam,
          employee: activeEmpId,
          employee_id: activeEmpId,
        };
        const listRes = await postData(listUrl, true, listParams);

        const rawObj =
          listRes.status === 200 && listRes.data?.data?.data
            ? listRes.data.data.data
            : {};

        setTotalExpCount(listRes.data?.data?.expensecount ?? 0);

        // Directly format all available expense data from the API
        let formattedData: any[] = [];
        if (Array.isArray(rawObj)) {
          formattedData = rawObj;
        } else if (rawObj && typeof rawObj === "object") {
          formattedData = Object.entries(rawObj).map(([dateKey, val]: [string, any]) => ({
            date: val?.date || val?.expenses?.ExpenseDate || dateKey,
            ...val,
          }));
        }

        // Sort chronologically by date
        formattedData.sort((a, b) => {
          return moment(a.date, "YYYY-MM-DD").valueOf() - moment(b.date, "YYYY-MM-DD").valueOf();
        });

        setExpenseData(formattedData);

        // Calculate total expense amount from actual data
        const total = formattedData.reduce((acc, item) => {
          if (item?.expenses?.ExpenseFinalAmt != null) {
            return acc + (Number(item.expenses.ExpenseFinalAmt) || 0);
          }
          if (item?.TotalExpenseAmount != null) {
            return acc + (Number(item.TotalExpenseAmount) || 0);
          }
          if (Array.isArray(item.expenseLine)) {
            const lineSum = item.expenseLine.reduce(
              (s: number, l: any) => s + (Number(l.ExpFinalAmount) || 0),
              0
            );
            return acc + lineSum;
          }
          return acc;
        }, 0);
        setTotalExpenseAmount(total);

        // 2. Fetch Working Days
        const workingDaysUrl = `${serverBaseUrl}${API.getWorkingDays}`;
        const workingDaysParams = { employee_id: activeEmpId, month: formattedMonthParam };
        const wdRes = await getData(workingDaysUrl, true, workingDaysParams);
        if (wdRes.status === 200) {
          setWorkingDays(wdRes.data?.data?.WorkingDays ?? null);
        }

        // 3. Fetch Last Dates
        const lastDatesUrl = `${serverBaseUrl}${API.getExpenseLastDates}`;
        const lastDatesParams = { employee_id: activeEmpId, month: monthParam };
        const ldRes = await getData(lastDatesUrl, true, lastDatesParams);
        if (ldRes.status === 200 && ldRes.data?.data) {
          setLastDates(ldRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching expense data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyExpenses();
  }, [selectedMonth, selectedRange, activeEmpId, serverUrl, refreshKey]);

  // Handle View click on row -> navigate to Expense Overview page
  const handleViewDetails = (item: any, index: number) => {
    const selectedExpId = item.expenses?.ExpId;
    if (!selectedExpId) return;

    const validExpenseList = expenseData.filter((i) => i?.expenses?.ExpId);
    const validIndex = validExpenseList.findIndex(
      (i) => i?.expenses?.ExpId === selectedExpId
    );

    navigate("/expenses/expenseoverview", {
      state: {
        expId: selectedExpId,
        doVerify: item.expenses?.DoVerify || false,
        expenseStatus: item.expenses?.ExpenseStatus,
        expenseList: validExpenseList,
        currentIndex: validIndex >= 0 ? validIndex : 0,
        isOwnUser: true,
        manager: false,
      },
    });
  };

  const getRowColor = (item: any) => {
    if (item?.expenses?.DoVerify) {
      return "#FFF9E6";
    }
    if (item?.WeekOff || item?.Holiday || item?.Leave || item?.Day === "Sunday") {
      return "#E6F4FA";
    }
    if (item?.FW < 4 || (item?.NCA > 0 && item?.FW > 0)) {
      return "#FDE8E8";
    }
    return "#FFFFFF";
  };

  const headData = [
    {
      id: "select",
      head: (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={(e) => toggleSelectAll(e.target.checked)}
        />
      ),
      key: "select",
    },
    { id: "date", head: "Expense Date", key: "date" },
    { id: "totalCalls", head: "Total Calls", key: "TotalCalls" },
    { id: "totalNCAs", head: `Total ${getLabel('NCA')}'s`, key: "NCA" },
    { id: "type", head: "Type", key: "expenses.TripType" },
    { id: "placeOfWork", head: "Place Of Work", key: "expenses.ExpensePlacewrk" },
    { id: "ta", head: "TA", key: "ta" },
    { id: "da", head: "DA", key: "da" },
    { id: "otherExpense", head: "Other Expense", key: "otherExpense" },
    { id: "totalExpense", head: "Total Expense", key: "expenses.ExpenseFinalAmt" },
    { id: "status", head: "Status", key: "expenses.ExpenseStatus" },
    { id: "action", head: "", key: "action" },
  ];

  return (
    <div className="p-3">
      {/* Header & Month Selector */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="m-0 fw-bold text-dark">
          <i className="fa fa-money me-2 text-primary"></i> Expenses Overview
        </h4>
        <div className="d-flex align-items-center gap-2">
          {!showPendingAttendanceWarning && allSubmittableIds.length > 0 && (
            <Button
              color="success"
              className="fw-bold text-white shadow-sm d-flex align-items-center"
              style={{
                backgroundColor: canSubmitExpenses ? "#28a745" : "#94a3b8",
                borderColor: canSubmitExpenses ? "#28a745" : "#94a3b8",
                cursor: canSubmitExpenses ? "pointer" : "not-allowed",
              }}
              title={
                !allCheckboxesSelected
                  ? "All checkboxes must be selected to submit"
                  : "Submit all expenses"
              }
              onClick={handleSubmitButtonClick}
            >
              <i className="fa fa-check me-2"></i>
              Submit All Expenses
            </Button>
          )}
          <MonthDropdown onChange={handleMonthChange} />
        </div>
      </div>

      {/* Summary Statistic Cards */}
      <Row className="g-3 mb-4">
        <Col lg={3} md={6} sm={12}>
          <Card className="border shadow-sm mb-0">
            <CardBody className="p-3">
              <div className="text-muted small fw-semibold">Total Expenses</div>
              <h4 className="m-0 fw-bold text-primary mt-1">₹ {totalExpenseAmount.toLocaleString()}</h4>
            </CardBody>
          </Card>
        </Col>

        <Col lg={3} md={6} sm={12}>
          <Card className="border shadow-sm mb-0">
            <CardBody className="p-3">
              <div className="text-muted small fw-semibold">Working Days</div>
              <h4 className="m-0 fw-bold text-dark mt-1">{workingDays !== null ? workingDays : "-"}</h4>
            </CardBody>
          </Card>
        </Col>

        {lastDates.LastSubmittedDate && (
          <Col lg={3} md={6} sm={12}>
            <Card className="border shadow-sm mb-0">
              <CardBody className="p-3">
                <div className="text-muted small fw-semibold">Submitted Date</div>
                <h5 className="m-0 fw-bold text-dark mt-1">
                  {moment(lastDates.LastSubmittedDate).format("DD MMM YYYY")}
                </h5>
              </CardBody>
            </Card>
          </Col>
        )}

        {lastDates.LastApprovedDate && (
          <Col lg={3} md={6} sm={12}>
            <Card className="border shadow-sm mb-0">
              <CardBody className="p-3">
                <div className="text-muted small fw-semibold">Approved Date</div>
                <h5 className="m-0 fw-bold text-success mt-1">
                  {moment(lastDates.LastApprovedDate).format("DD MMM YYYY")}
                </h5>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      {/* Warning banner above table if dates pending for attendance */}
      {showPendingAttendanceWarning && (
        <div
          className="d-flex align-items-center justify-content-center py-2 px-3 my-3 text-white fw-bold rounded shadow-sm"
          style={{ backgroundColor: "#f97316", fontSize: "16px" }}
        >
          <i className="fa fa-exclamation-triangle me-2 fs-5"></i>
          Some dates are pending for attendance.
        </div>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={submitConfirmationVisible}
        toggle={() => setSubmitConfirmationVisible(false)}
        centered
      >
        <ModalHeader toggle={() => setSubmitConfirmationVisible(false)}>
          Submit Expenses
        </ModalHeader>
        <ModalBody>
          Are you sure you want to submit all the selected expenses?
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setSubmitConfirmationVisible(false)}
          >
            Cancel
          </Button>{" "}
          <Button
            color="success"
            onClick={() => {
              setSubmitConfirmationVisible(false);
              setSubmitRemarkModalVisible(true);
            }}
          >
            Confirm
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remark Modal */}
      <Modal
        isOpen={submitRemarkModalVisible}
        toggle={() => setSubmitRemarkModalVisible(false)}
        centered
      >
        <ModalHeader toggle={() => setSubmitRemarkModalVisible(false)}>
          Submit Expenses
        </ModalHeader>
        <ModalBody>
          <div className="mb-2">
            <label className="form-label fw-semibold text-dark">Remark</label>
            <Input
              type="textarea"
              rows={3}
              placeholder="Enter remark"
              value={submitRemark}
              onChange={(e) => setSubmitRemark(e.target.value)}
            />
            <small className="d-block mt-2" style={{ color: "#f97316" }}>
              Note: Remark is not mandatory.
            </small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setSubmitRemarkModalVisible(false)}
          >
            Cancel
          </Button>{" "}
          <Button
            color="primary"
            disabled={isSubmitting}
            onClick={() => submitExpenseAction(2, submitRemark)}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Expenses Table */}
      {loading ? (
        <div className="text-center py-3" style={{ minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div>
            <Image
              className="b-r-10 img-80"
              src={dynamicImage("dashboard/loader.gif")}
              alt="Loading..."
            />
          </div>
        </div>
      ) : (
        <div className="border rounded bg-white shadow-sm overflow-hidden table-responsive">
          <CommonTableNew
            tableClass="table caption align-middle mb-0"
            strip={false}
            caption=""
            size="sm"
            hover
            hidePagination={true}
            headData={headData}
            data={expenseData}
            renderRow={(item: any, index: number) => {
              const hasType = item?.hasOwnProperty("Type");
              const ta =
                item.expenseLine?.find((l: any) =>
                  l.ExpenseMaster?.ExpenseName?.toLowerCase().includes("ta")
                )?.ExpFinalAmount || item.ta || 0;
              const da =
                item.expenseLine?.find((l: any) =>
                  l.ExpenseMaster?.ExpenseName?.toLowerCase().includes("da")
                )?.ExpFinalAmount || item.da || 0;
              const totalAmt = item.expenses?.ExpenseFinalAmt || item.TotalExpenseAmount || 0;
              const otherExpense = totalAmt > 0 ? totalAmt - (Number(ta) + Number(da)) : 0;
              const expenseStatus = Number(item.expenses?.ExpenseStatus);
              const statusLabel = !hasType && expenseStatus
                ? statusMap[expenseStatus] || "Submitted"
                : item.Day || "-";

              const expId = String(item?.expenses?.ExpId ?? item?.ExpId ?? "");
              const isChecked = expId ? selectedExpIds.includes(expId) : false;

              return (
                <tr key={index} style={{ backgroundColor: getRowColor(item) }}>
                  <td className="px-3" style={{ width: "30px" }}>
                    {!hasType && expId ? (
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleCheckboxToggle}
                      />
                    ) : null}
                  </td>
                  <td className="fw-semibold px-3">
                    {moment(item.date, "YYYY-MM-DD").format("DD MMM")}
                  </td>
                  <td className="px-3">{item.TotalCalls || "-"}</td>
                  <td className="px-3">{item.NCA || "-"}</td>
                  <td className="px-3">{item.expenses?.TripType || "-"}</td>
                  <td className="px-3">{item.expenses?.ExpensePlacewrk || "-"}</td>
                  <td className="px-3">{ta}</td>
                  <td className="px-3">{da}</td>
                  <td className="px-3">{otherExpense > 0 ? otherExpense : "-"}</td>
                  <td className="fw-bold px-3">{totalAmt > 0 ? totalAmt : "-"}</td>
                  <td className="px-3">{statusLabel}</td>
                  <td className="px-3 text-end">
                    {!hasType && item?.expenses?.ExpId ? (
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => handleViewDetails(item, index)}
                      >
                        View
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            }}
          />

          {/* Bottom Action Area matching mobile apk */}
          {!showPendingAttendanceWarning && allSubmittableIds.length > 0 ? (
            <div className="p-3 w-100 d-flex justify-content-center">
              <Button
                color="success"
                className="w-100 py-2 fw-bold text-white shadow-sm d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: canSubmitExpenses ? "#28a745" : "#94a3b8",
                  borderColor: canSubmitExpenses ? "#28a745" : "#94a3b8",
                  cursor: canSubmitExpenses ? "pointer" : "not-allowed",
                  fontSize: "16px",
                }}
                title={
                  !allCheckboxesSelected
                    ? "All checkboxes must be selected to submit"
                    : "Submit all expenses"
                }
                onClick={handleSubmitButtonClick}
              >
                <i className="fa fa-check me-2"></i>
                Submit All Expenses
              </Button>
            </div>
          ) : (
            showPendingAttendanceWarning && (
              <div
                className="d-flex align-items-center justify-content-center py-2 px-3 m-3 text-white fw-bold rounded shadow-sm"
                style={{ backgroundColor: "#f97316", fontSize: "16px" }}
              >
                <i className="fa fa-exclamation-triangle me-2 fs-5"></i>
                Some dates are pending for attendance.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default UserExpenses;
